import { type FC } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { useReviewRedirect } from "@/pages/postloss/modules/hooks/useReviewRedirect";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  OtherLoanForm,
  parseItemToForm,
  validateOtherLoanFields,
  otherLoanToItemFields,
  type OtherLoanFormValues,
} from "./OtherLoanForm";
import { buildLoanSubtitle } from "./debtSubtitles";

export const OtherLoansReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { debts, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);

  const items = filterByCategory(debts, "Unsecured Debts").filter(
    (item) => !!item.item_type && !item.item_type.toLowerCase().includes("credit"),
  );
  const firstName = person?.first_name ?? "their";
  const hasExtracted = items.some((p) => p.extracted_from_document);

  const { itemsEverFound, alreadyRedirected } = useReviewRedirect({
    items,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "review_other_loans",
    addKey: "add_other_loan",
    fallbackScreen: "other_loans",
    fallbackAnswers: {
      ...answers,
      review_other_loans: "none",
      add_other_loan: "",
    },
  });

  const isRedirecting = !isLoading && items.length === 0 && !itemsEverFound && !alreadyRedirected;

  const handleSaveEdit = async (editing: {
    id: number;
    form: OtherLoanFormValues;
    existingFileName: string | null;
    fileRemoved: boolean;
  }) => {
    if (!estateId) return;

    const validationError = Object.values(validateOtherLoanFields(editing.form))[0];
    if (validationError) throw new Error(validationError);

    let sourceDocumentId: number | undefined;

    if (editing.form.statementFile) {
      sourceDocumentId = await uploadDocumentWithAuth(
        editing.form.statementFile,
        estateId,
        "loan_statement",
      );
    }

    const sourceDocUpdate = sourceDocumentId
      ? { source_document_id: sourceDocumentId }
      : editing.fileRemoved
        ? { source_document_id: null }
        : {};

    const fields = otherLoanToItemFields(editing.form);
    await updateItem(editing.id, {
      item_name: fields.item_name,
      item_type: fields.item_type,
      balance: fields.balance,
      metadata: fields.metadata,
      ...sourceDocUpdate,
    });
  };

  return (
    <ReviewScreenLayout<OtherLoanFormValues>
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={isRedirecting}
      screenTitle={
        hasExtracted
          ? `We uncovered a loan in ${firstName}'s documents`
          : `Review ${firstName}'s loans`
      }
      screenSubtitle="Review the details and add any others we might have missed."
      itemLabel="loan"
      listTitle="Loans"
      onAddAnother={() =>
        goToScreen("add_other_loan", {
          ...answers,
          add_other_loan: "adding",
        })
      }
      onContinue={() => {
        setAnswer("add_other_loan", "");
        onComplete();
      }}
      renderSubtitle={buildLoanSubtitle}
      editDialogTitle="Edit loan"
      parseItemToForm={parseItemToForm}
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <OtherLoanForm
          form={form}
          onChange={onChange}
          existingFileName={existingFileName}
          onExistingFileRemove={onFileRemove}
        />
      )}
      onSaveEdit={handleSaveEdit}
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      deleteDialogTitle="Delete Loan"
      itemActions={itemActions}
    />
  );
};
